import { Button, Input } from '@chakra-ui/react';
import React, { useState, useEffect, useRef } from 'react';
import { eventListType, eventType } from '../common/types';
import { calcRecordByName, calcScoreByName } from './components/calcPoint';
import { eventNameDict } from './components/eventNameDict';
import { HowToUse } from './components/howToUse';
import { Links } from './components/links';

type AthleteType = {
  name: string;
  records: eventListType;
  points: eventListType;
};

const initialEventList: eventListType = {
  i00: '',
  lj: '',
  sp: '',
  hj: '',
  A00: '',
  ii0h: '',
  dt: '',
  pv: '',
  jt: '',
  i500: '',
};

const LOCAL_STORAGE_KEY = 'decathlon_compare_athletes';

export const CalculatorMastersDecathlonCompare = () => {
  const initialAthletesRef = useRef<AthleteType[]>([
    {
      name: '自分',
      records: initialEventList,
      points: initialEventList,
    },
    {
      name: '相手',
      records: initialEventList,
      points: initialEventList,
    },
  ]);

  const [athletes, setAthletes] = useState<AthleteType[]>(() => {
    const saved = localStorage.getItem(LOCAL_STORAGE_KEY);
    return saved
      ? (JSON.parse(saved) as AthleteType[])
      : initialAthletesRef.current;
  });

  useEffect(() => {
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(athletes));
  }, [athletes]);

  const eventList = Object.keys(initialEventList) as eventType[];

  const handleChange = (
    athleteIndex: number,
    key: eventType,
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const val = Number(e.target.value);
    setAthletes((prev: AthleteType[]) =>
      prev.map((athlete: AthleteType, i) =>
        i === athleteIndex
          ? {
              ...athlete,
              records: { ...athlete.records, [key]: val },
              points: {
                ...athlete.points,
                [key]: calcScoreByName(key, val),
              },
            }
          : athlete
      )
    );
  };

  const handlePointChange = (
    athleteIndex: number,
    key: eventType,
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const val = Number(e.target.value);
    setAthletes((prev: AthleteType[]) =>
      prev.map((athlete: AthleteType, i) =>
        i === athleteIndex
          ? {
              ...athlete,
              points: { ...athlete.points, [key]: val },
              records: {
                ...athlete.records,
                [key]: calcRecordByName(key, val),
              },
            }
          : athlete
      )
    );
  };

  const calcTotalPoint = (points: eventListType, EventName: eventType) => {
    let totalPoint = 0;
    for (const event in points) {
      const score = Number(points[event as eventType]) || 0;
      totalPoint += score;
      if (event === EventName) break;
    }
    return totalPoint;
  };

  const updateAthleteName = (index: number, name: string) => {
    setAthletes((prev: AthleteType[]) =>
      prev.map((a, i) => (i === index ? { ...a, name } : a))
    );
  };

  const addAthlete = () => {
    const newAthleteIndex = athletes.length;
    setAthletes((prev: AthleteType[]) => [
      ...prev,
      {
        name: `選手${newAthleteIndex}`,
        records: initialEventList,
        points: initialEventList,
      },
    ]);
  };

  const removeAthlete = (index: number) => {
    setAthletes((prev: AthleteType[]) => prev.filter((_, i) => i !== index));
  };

  const reset = () => {
    setAthletes((prev: AthleteType[]) =>
      prev.map((athlete: AthleteType) => ({
        ...athlete,
        records: initialEventList,
        points: initialEventList,
      }))
    );
  };

  const clearAllAthletes = () => {
    setAthletes([]);
  };

  return (
    <div>
      <div
        style={{ marginLeft: '20px', marginTop: '16px', marginBottom: '16px' }}
      >
        マスターズ十種競技得点表（ライバル比較Ver）
      </div>

      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '8px',
          marginBottom: '10px',
          marginLeft: '36px',
        }}
      >
        <HowToUse />
        <Button
          onClick={reset}
          size="xs"
          style={{ color: 'black', backgroundColor: '#f5f5f5' }}
        >
          Reset
        </Button>
        <Button
          onClick={addAthlete}
          size="xs"
          style={{ color: 'black', backgroundColor: '#e0f7fa' }}
        >
          + 選手追加
        </Button>
        <Button
          onClick={clearAllAthletes}
          size="xs"
          style={{ color: 'black', backgroundColor: '#ffe0e0' }}
        >
          🚫 全削除
        </Button>
      </div>

      {eventList.map((eN, index) => {
        const bgColor = index % 2 === 0 ? '#f5f5f5' : '#ffffff';

        return (
          <div key={index} style={{ backgroundColor: bgColor, padding: '8px' }}>
            <div
              style={{
                fontWeight: 'bold',
                marginBottom: '4px',
                marginLeft: '16px',
              }}
            >
              {eventNameDict[eN]}
            </div>
            {athletes.map((athlete, athleteIndex) => (
              <div
                key={athleteIndex}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginLeft: '10px',
                  marginBottom: '4px',
                  flexWrap: 'wrap',
                }}
              >
                <Input
                  type="text"
                  value={athlete.name}
                  onChange={(e) =>
                    updateAthleteName(athleteIndex, e.target.value)
                  }
                  size="xs"
                  width="80px"
                  fontSize="12px"
                  backgroundColor="white"
                />
                <Input
                  type="text"
                  placeholder="記録"
                  inputMode="numeric"
                  backgroundColor="white"
                  size="xs"
                  maxLength={5}
                  value={athlete.records[eN]}
                  onChange={(e) => handleChange(athleteIndex, eN, e)}
                  style={{ width: '60px', fontSize: '12px', padding: '4px' }}
                />
                <Input
                  type="text"
                  placeholder="得点"
                  inputMode="numeric"
                  backgroundColor="white"
                  size="xs"
                  maxLength={4}
                  value={athlete.points[eN]}
                  onChange={(e) => handlePointChange(athleteIndex, eN, e)}
                  style={{ width: '60px', fontSize: '12px', padding: '4px' }}
                />
                <div
                  style={{ width: '120px', fontSize: '12px', color: '#222' }}
                >
                  合計: {calcTotalPoint(athlete.points, eN)} 点
                  {athletes.length > 2 && athleteIndex !== 0 && (
                    <Button
                      onClick={() => removeAthlete(athleteIndex)}
                      size="xs"
                      colorScheme="red"
                      variant="ghost"
                    >
                      削除
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        );
      })}

      {athletes.map((athlete, idx) => (
        <div
          key={idx}
          style={{
            backgroundColor: '#f5f5f5',
            marginTop: '12px',
            padding: '6px',
            borderRadius: '6px',
          }}
        >
          <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
            {athlete.name} 合計点
          </div>
          <div style={{ display: 'flex', width: '360px' }}>
            <div style={{ width: '30%', marginLeft: '10pt' }}>
              1日目：{calcTotalPoint(athlete.points, 'A00')}
            </div>
            <div style={{ width: '30%', marginLeft: '10pt' }}>
              2日目：
              {calcTotalPoint(athlete.points, 'i500') -
                calcTotalPoint(athlete.points, 'A00')}
            </div>
            <div style={{ width: '20%', textAlign: 'right' }}>合計：</div>
            <div
              style={{ width: '20%', textAlign: 'right', marginRight: '1pt' }}
            >
              {calcTotalPoint(athlete.points, 'i500')} 点
            </div>
          </div>
        </div>
      ))}

      <Links />
    </div>
  );
};
